
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight, MessageSquare } from 'lucide-react';
import { supabase } from '@/lib/customSupabaseClient';
import { Button } from '@/components/ui/button';

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [whatsappNumber, setWhatsappNumber] = useState('5555984328501');

  useEffect(() => {
    fetchPlans();
    fetchSettings();
  }, []);

  const fetchPlans = async () => {
    try {
      const { data, error } = await supabase
        .from('plans')
        .select('*')
        .order('order', { ascending: true });

      if (error) throw error;
      setPlans(data || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const { data } = await supabase
        .from('settings')
        .select('json_data')
        .eq('company_name', 'whatsapp_settings')
        .single();

      if (data?.json_data?.phone) {
        setWhatsappNumber(data.json_data.phone);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleContactClick = (planName) => {
    const message = encodeURIComponent(
      `Olá! Gostaria de saber mais sobre o plano ${planName}.`
    );
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
  };

  if (isLoading) {
    return (
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4 flex justify-center">
          <div className="w-12 h-12 border-4 border-[#0a4b3c] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </section>
    );
  }

  if (plans.length === 0) {
    return null;
  }

  return (
    <section id="plans" className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50 overflow-hidden">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#c99b36]/10 text-[#c99b36] rounded-full font-semibold mb-6">
            <Check className="w-4 h-4" />
            <span>Nossos Planos</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0a4b3c] mb-6">
            Escolha o Plano Ideal para Você
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Soluções personalizadas para cada necessidade. Comece sua jornada com a melhor assessoria contábil.
          </p>
        </motion.div>

        <div className={`grid gap-8 ${
          plans.length === 1 
            ? 'max-w-md mx-auto' 
            : plans.length === 2 
            ? 'md:grid-cols-2 max-w-4xl mx-auto'
            : 'md:grid-cols-2 lg:grid-cols-3'
        }`}>
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -10, scale: 1.02 }}
              className="group relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#0a4b3c]/20 to-[#c99b36]/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100">
                {/* Decorative Top Bar */}
                <div className="h-2 bg-gradient-to-r from-[#0a4b3c] to-[#c99b36]"></div>
                
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {plan.name}
                  </h3>
                  
                  {plan.description && (
                    <p className="text-gray-600 mb-6 min-h-[60px]">
                      {plan.description}
                    </p>
                  )}

                  {plan.show_price && plan.price && (
                    <div className="mb-6">
                      <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-bold text-[#0a4b3c]">
                          R$ {plan.price.toFixed(2)}
                        </span>
                        <span className="text-gray-500">/mês</span>
                      </div>
                    </div>
                  )}

                  {plan.button_type === 'contact' ? (
                    <Button
                      onClick={() => handleContactClick(plan.name)}
                      className="w-full bg-[#0a4b3c] hover:bg-[#063127] text-white py-6 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group"
                    >
                      <MessageSquare className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                      Entre em contato
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  ) : (
                    <div className="text-center py-6 px-4 bg-gradient-to-r from-[#0a4b3c]/10 to-[#c99b36]/10 rounded-xl">
                      <p className="text-3xl font-bold text-[#0a4b3c]">
                        R$ {plan.price?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">por mês</p>
                    </div>
                  )}
                </div>

                {/* Decorative Bottom Element */}
                <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-[#c99b36]/10 to-transparent rounded-tl-full opacity-50"></div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Plans;
